/**
 * Campaign Order Data Validator
 *
 * Validates that a CampaignOrder GraphQL response has the minimum nested
 * depth required before we let form initialization or render logic run.
 * This prevents crashes caused by Apollo cache serving partially-populated
 * objects or by backend responses with unexpectedly-null arrays.
 */

/**
 * Check whether a value is a non-null object (and not an array).
 */
const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Check whether a value is an array (empty arrays are valid).
 */
const isArray = (value) => Array.isArray(value);

/**
 * Validate a single line item from GraphQL.
 * Minimum viable shape: { id, name } plus optional nested arrays.
 */
const isValidLineItem = (lineItem) => {
  if (!isObject(lineItem)) return false;
  // id can be missing for newly-created line items (form state), so we only
  // require name-like fields or budget fields to consider it "dataful".
  const hasAnyField =
    lineItem.name !== undefined ||
    lineItem.budgetTotal !== undefined ||
    lineItem.startDate !== undefined ||
    lineItem.adGroup !== undefined;
  return hasAnyField;
};

/**
 * Validate a single campaign from GraphQL.
 */
const isValidCampaign = (campaign) => {
  if (!isObject(campaign)) return false;
  // Campaign must at least have an id (for existing) or a name/budget (for new).
  const hasIdentity = campaign.id !== undefined || campaign.name !== undefined || campaign.budgetTotal !== undefined;
  if (!hasIdentity) return false;

  // If lineItems is present, it must be an array (even empty).
  if (campaign.lineItems !== undefined && campaign.lineItems !== null && !isArray(campaign.lineItems)) {
    return false;
  }

  // If lineItems is an array, filter out null elements but don't require non-empty.
  if (isArray(campaign.lineItems)) {
    const validLineItems = campaign.lineItems.filter(isValidLineItem);
    // It's okay if some elements are null (backend sometimes returns sparse arrays).
    // We just need the array structure itself to be present.
    return true;
  }

  return true;
};

/**
 * Validate a CampaignOrder object for the requesting/edit flow.
 *
 * @param {object} campaignOrder – the GraphQL response
 * @param {object} options
 * @param {boolean} options.requireCampaigns – if true, campaigns array must exist (default: true for edit)
 * @returns {object} { isReady: boolean, missingFields: string[], campaigns: array }
 */
export const validateCampaignOrderForRequestor = (campaignOrder, options = {}) => {
  const { requireCampaigns = true } = options;
  const missingFields = [];

  if (!isObject(campaignOrder)) {
    missingFields.push('campaignOrder');
    return { isReady: false, missingFields, campaigns: [] };
  }

  // Minimum order-level fields
  if (campaignOrder.id === undefined && campaignOrder.name === undefined) {
    missingFields.push('id|name');
  }

  if (requireCampaigns) {
    if (!isArray(campaignOrder.campaigns)) {
      missingFields.push('campaigns');
    } else {
      const validCampaigns = campaignOrder.campaigns.filter(isValidCampaign);
      if (validCampaigns.length === 0 && campaignOrder.campaigns.length > 0) {
        // Array exists but every element is malformed
        missingFields.push('campaigns[all-malformed]');
      }
    }
  }

  // Check advertiser if present (optional for new orders)
  if (campaignOrder.advertiser !== undefined && campaignOrder.advertiser !== null && !isObject(campaignOrder.advertiser)) {
    missingFields.push('advertiser');
  }

  const isReady = missingFields.length === 0;
  const campaigns = isArray(campaignOrder.campaigns)
    ? campaignOrder.campaigns.filter(isValidCampaign)
    : [];

  return { isReady, missingFields, campaigns };
};

/**
 * Validate a CampaignOrder object for the queue/launch flow.
 *
 * @param {object} campaignOrder – the GraphQL response
 * @returns {object} { isReady: boolean, missingFields: string[] }
 */
export const validateCampaignOrderForQueue = (campaignOrder) => {
  const missingFields = [];

  if (!isObject(campaignOrder)) {
    missingFields.push('campaignOrder');
    return { isReady: false, missingFields };
  }

  if (!campaignOrder.id) {
    missingFields.push('id');
  }

  if (!isArray(campaignOrder.campaigns)) {
    missingFields.push('campaigns');
  }

  // dspConfig is optional (not all orders have programmatic launch)
  // but if it's present it should be an object or scalar (JSON field).

  const isReady = missingFields.length === 0;
  return { isReady, missingFields };
};
